import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

export const inputCls = "w-full bg-transparent border border-vv-black/20 focus:border-vv-black outline-none px-3 py-2.5 text-sm placeholder:text-vv-stone transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

export const FIELD_GROUPS = {
  details: ["name", "sku", "gender", "category", "subcategory", "tags", "fit"],
  content: ["description", "fabric", "gsm", "wash_care", "seo_title", "seo_description"],
  price: ["price", "compare_at"],
  cost: ["cost_price"],
  stock: ["stock", "size_stock"],
  images: ["images"],
  visibility: ["status", "featured", "new_arrival", "new_arrival_order"],
};

const FIELD_TO_GROUP = {};
Object.entries(FIELD_GROUPS).forEach(([g, fs]) => fs.forEach((f) => { FIELD_TO_GROUP[f] = g; }));

export const fieldLevel = (user, group) =>
  user?.role === "admin" ? "edit" : (user?.permissions?.product_fields?.[group] || "none");

export const canDo = (user, module, action) =>
  user?.role === "admin" || (user?.permissions?.[module] || []).includes(action);

export const fullUrl = (src) => (src?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src);

export const EMPTY = {
  name: "", category: "tshirts", subcategory: "", gender: "men", price: "", compare_at: "", cost_price: "", sku: "",
  status: "published", description: "", fabric: "", gsm: "", fit: "", wash_care: "",
  seo_title: "", seo_description: "",
  colors: "Off White:#FDFBF7", sizes: "S,M,L,XL,XXL", size_stock: {}, images: [],
  tags: "", featured: false, new_arrival: false, new_arrival_order: "", stock: 100,
};

export function toForm(p) {
  return {
    ...EMPTY, ...p,
    price: String(p.price ?? ""), compare_at: p.compare_at ? String(p.compare_at) : "",
    cost_price: p.cost_price != null ? String(p.cost_price) : "",
    new_arrival_order: p.new_arrival_order != null ? String(p.new_arrival_order) : "",
    gsm: p.gsm ? String(p.gsm) : "",
    colors: (p.colors || []).map((c) => `${c.name}:${c.hex}`).join(", "),
    sizes: (p.sizes || []).join(", "),
    size_stock: p.size_stock || {},
    images: p.images || [],
    tags: (p.tags || []).join(", "),
  };
}

export function fromForm(f, user) {
  const all = {
    name: f.name, category: f.category, subcategory: f.subcategory, gender: f.gender,
    price: parseFloat(f.price) || 0, compare_at: f.compare_at ? parseFloat(f.compare_at) : null,
    cost_price: f.cost_price ? parseFloat(f.cost_price) : null,
    sku: f.sku, status: f.status, description: f.description, fabric: f.fabric,
    gsm: f.gsm ? parseInt(f.gsm) : null, fit: f.fit, wash_care: f.wash_care,
    seo_title: f.seo_title, seo_description: f.seo_description,
    colors: f.colors.split(",").map((s) => s.trim()).filter(Boolean).map((s) => {
      const [name, hex] = s.split(":").map((x) => x.trim());
      return { name: name || "", hex: hex || "#000000" };
    }),
    sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
    size_stock: f.size_stock || {},
    images: f.images,
    tags: f.tags.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
    featured: !!f.featured, new_arrival: !!f.new_arrival,
    new_arrival_order: f.new_arrival_order !== "" && f.new_arrival_order != null ? parseInt(f.new_arrival_order) : null,
    stock: parseInt(f.stock) || 0,
  };
  if (user?.role === "admin") return all;
  const out = {};
  for (const [k, v] of Object.entries(all)) {
    const g = FIELD_TO_GROUP[k];
    if (!g || fieldLevel(user, g) === "edit") out[k] = v;
  }
  return out;
}

export function ImageManager({ images, onChange, disabled = false }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange([...images, data.url]);
      }
      toast.success(files.length > 1 ? `${files.length} images uploaded` : "Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const move = (i, dir) => {
    const next = [...images];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">Images — first image is the cover</label>
      <div className="flex flex-wrap gap-3 mt-2">
        {images.map((src, i) => (
          <div key={src + i} className="relative w-20 aspect-[3/4] bg-vv-elevated group">
            <img src={fullUrl(src)} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute bottom-0 inset-x-0 text-center text-[8px] tracking-widest bg-vv-black/70 text-vv-ivory py-0.5">COVER</span>}
            {!disabled && (
              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => move(i, -1)} className="bg-vv-ivory p-0.5" aria-label="Move left"><ChevronLeft size={11} /></button>
                <button type="button" onClick={() => move(i, 1)} className="bg-vv-ivory p-0.5" aria-label="Move right"><ChevronRight size={11} /></button>
                <button type="button" data-testid={`image-remove-${i}`} onClick={() => onChange(images.filter((_, x) => x !== i))} className="bg-vv-ivory p-0.5 text-vv-burgundy" aria-label="Remove"><X size={11} /></button>
              </div>
            )}
          </div>
        ))}
        {!disabled && (
          <>
            <button type="button" data-testid="image-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="w-20 aspect-[3/4] border border-dashed border-vv-black/25 flex flex-col items-center justify-center gap-1 text-vv-muted hover:border-vv-black transition-colors">
              <Upload size={16} strokeWidth={1.5} />
              <span className="text-[9px] uppercase tracking-widest">{uploading ? "…" : "Upload"}</span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid="image-upload-input" />
          </>
        )}
      </div>
      {!disabled && (
        <input value={images.join("\n")} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
          placeholder="…or paste image URLs, one per line" className={`${inputCls} mt-3 font-mono text-xs`} data-testid="form-images" />
      )}
    </div>
  );
}
